import { type Medal, medalFor } from '../game/medals';
import { formatTicks } from '../game/RunTimer';
import type { Records } from '../game/records';
import type { MapDef } from '../world/maps';

const MEDAL_ORDER: Medal[] = ['author', 'gold', 'silver', 'bronze'];

/**
 * Records panel on the start overlay: the selected map's medal table and its
 * local top-10 runs. Call `render(def)` on map switch and after each finish.
 */
export class LeaderboardPanel {
  constructor(
    private readonly root: HTMLElement,
    private readonly records: Records,
  ) {}

  render(def: MapDef): void {
    this.root.replaceChildren();

    const title = document.createElement('h3');
    title.textContent = def.name;
    this.root.appendChild(title);

    if (!def.medals) {
      const note = document.createElement('p');
      note.className = 'lb-note';
      note.textContent = 'Untimed map — surf freely.';
      this.root.appendChild(note);
      return;
    }

    const record = this.records.maps[def.id];
    const pb = record?.pbTicks;

    // Medal table: tier name, target time, and a check if the PB earns it.
    const medals = document.createElement('div');
    medals.className = 'lb-medals';
    for (const tier of MEDAL_ORDER) {
      const target = def.medals[tier];
      const earned = pb !== undefined && pb <= target;
      const row = document.createElement('div');
      row.className = `lb-medal lb-${tier}${earned ? ' earned' : ''}`;
      row.innerHTML = `<span class="lb-dot"></span><span class="lb-tier">${tier}</span><span class="lb-time">${formatTicks(target)}</span>`;
      medals.appendChild(row);
    }
    this.root.appendChild(medals);

    // Top-10 runs.
    const runs = record?.runs ?? [];
    const list = document.createElement('ol');
    list.className = 'lb-runs';
    if (runs.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'lb-note';
      empty.textContent = 'No runs yet — set a time.';
      this.root.appendChild(empty);
      return;
    }
    for (const run of runs.slice(0, 10)) {
      const li = document.createElement('li');
      const medal = medalFor(run.ticks, def.medals);
      const date = run.date ? new Date(run.date).toLocaleDateString() : '';
      li.innerHTML = `<span class="lb-time">${formatTicks(run.ticks)}</span><span class="lb-run-medal lb-${medal ?? 'none'}"></span><span class="lb-date">${date}</span>`;
      list.appendChild(li);
    }
    this.root.appendChild(list);
  }
}

/** Small colored dot for a map-select button showing the earned medal (or none). */
export function medalBadgeClass(pb: number | undefined, def: MapDef): string {
  if (pb === undefined || !def.medals) {
    return 'lb-none';
  }
  return `lb-${medalFor(pb, def.medals) ?? 'none'}`;
}
